/**
 *
 * The notifications duck. Default export is the reducer.
 *
 * Action Types:
 * - NOTIFICATIONS_FETCH_REQUESTED
 * - NOTIFICATIONS_FETCH_SUCCEEDED
 * - NOTIFICATIONS_FETCH_FAILED
 * - NOTIFICATIONS_RESET
 * - NOTIFICATIONS_READ
 * - NOTIFICATIONS_CLEAR_SUCCEEDED
 * - NOTIFICATIONS_CLEAR_FAILED
 *
 * Action Creators:
 * - notificationsRequest
 * - notificationsSucceeded
 * - notificationsFailed
 * - notificationsReset
 * - noficiationsRead
 * - notificationsClearSucceeded
 * - notificationsClearFailed
 */

import get from 'lodash/get';
// $FlowFixMe
import format from 'date-fns/format';

import type {
  Notification,
  NotificationsState,
  NotificationAction,
  NotificationsFetchRequested,
  NotificationsFetchSucceeded,
  NotificationsFetchFailed,
  NotificationsReset,
  NotificationsRead,
  NotificationsClearSucceeded,
  NotificationsClearFailed,
} from '../types';

/** @type {ActionType} */
export const NOTIFICATIONS_FETCH_REQUESTED =
  'PB/notifications/NOTIFICATIONS_FETCH_REQUESTED';

/** @type {ActionType} */
export const NOTIFICATIONS_FETCH_SUCCEEDED =
  'PB/notifications/NOTIFICATIONS_FETCH_SUCCEEDED';

/** @type {ActionType} **/
export const NOTIFICATIONS_RESET = 'PB/notifications/NOTIFICATIONS_RESET';

/** @type {ActionType} **/
export const NOTIFICATIONS_FETCH_FAILED =
  'PB/notifications/NOTIFICATIONS_FETCH_FAILED';

/** @type {ActionType} **/
export const NOTIFICATIONS_READ = 'PB/notifications/NOTIFICATIONS_READ';

/** @type {ActionType} **/
export const NOTIFICATIONS_CLEAR_SUCCEEDED =
  'PB/notifications/NOTIFICATIONS_CLEAR_SUCCEEDED';

/** @type {ActionType} **/
export const NOTIFICATIONS_CLEAR_FAILED =
  'PB/notifications/NOTIFICATIONS_CLEAR_FAILED';

// "containsNew" indicates whether there are any unread notifications or not.
// "list" is the array of notifications as pulled from the API.
export const initialState: NotificationsState = {
  containsNew: false,
  list: [],
};

/** @ignore */
export default function notifications(
  state: NotificationsState = initialState,
  action: NotificationAction,
): NotificationsState {
  switch (action.type) {
    case NOTIFICATIONS_FETCH_SUCCEEDED:
      const list = get(action, 'payload.list', []);
      const newNotifications = get(action, 'payload.containsNew', false);
      return { ...state, list, containsNew: newNotifications };

    case NOTIFICATIONS_READ:
      const oldList = state.list;
      const newList = oldList.map(notification => {
        if (!notification.readAt) {
          notification.readAt =
            format(Date.now(), 'YYYY-MM-DD[T]HH:mm:ss') + 'Z';
        }
        return notification;
      });
      return { ...state, containsNew: false, list: newList };

    case NOTIFICATIONS_RESET:
      return { ...state, containsNew: false, list: [] };

    default:
      return state;
  }
}

export function notificationsRequest(): NotificationsFetchRequested {
  return {
    type: NOTIFICATIONS_FETCH_REQUESTED,
  };
}

export function notificationsSucceeded(
  newNotifications: boolean,
  list: Array<Notification>,
): NotificationsFetchSucceeded {
  return {
    type: NOTIFICATIONS_FETCH_SUCCEEDED,
    payload: {
      list,
      containsNew: newNotifications,
    },
  };
}

export function notificationsFailed(message: string): NotificationsFetchFailed {
  return {
    type: NOTIFICATIONS_FETCH_FAILED,
    payload: {
      message,
    },
  };
}

export function notificationsReset(): NotificationsReset {
  return {
    type: NOTIFICATIONS_RESET,
  };
}

export function notificationsRead(): NotificationsRead {
  return {
    type: NOTIFICATIONS_READ,
  };
}

export function notificationsClearSucceeded(): NotificationsClearSucceeded {
  return {
    type: NOTIFICATIONS_CLEAR_SUCCEEDED,
  };
}

export function notificationsClearFailed(
  message: string,
): NotificationsClearFailed {
  return {
    type: NOTIFICATIONS_CLEAR_FAILED,
    payload: {
      message,
    },
  };
}
